/**
 * Guided Demos reservation for AUTOMATIC background live scheduling only.
 *
 * - DemoContext reserves synchronously when a guided run starts preparing
 *   (before any await or snapshot) and releases its OWN token when the run
 *   fully ends (after restoration and prior-view restore).
 * - Scoped to one instance plus its container (the Demos instance/project).
 *   Other instances/containers are never suspended, never drained and never
 *   see their epoch change. Nothing is stored; the setting is untouched.
 * - Background saves are admitted and tracked in one synchronous step, keyed
 *   by the save's scope (every instance/container its tasks could belong to),
 *   so a reserved scope refuses new saves and `drainLiveSaves(scope)` waits
 *   only for saves intersecting that scope.
 * - `scopedLiveEpoch(scope)` changes only when a reservation touching that
 *   scope is taken or released; a save admitted under an older scoped epoch
 *   must not apply its result.
 * Manual writes (gated task edits, reschedule gate) are not affected here.
 */
export interface LiveSaveScope {
  /** Every instance the planned tasks may belong to (tracked + loaded). */
  instanceIds: ReadonlyArray<string | null | undefined>;
  /** Loaded container/project ids the planned rows belong to. */
  containerIds?: ReadonlyArray<string | null | undefined>;
}
interface Reservation { token: symbol; instanceId: string; containerId: string | null }

let reservation: Reservation | null = null;
let globalEpoch = 0;
const scopeEpochs = new Map<string, number>(); // key: `i:<id>` / `c:<id>`
const listeners = new Set<() => void>();
const inFlight = new Map<Promise<unknown>, { instanceIds: Set<string>; containerIds: Set<string> }>();
// Independent container-only copy holds must never replace a guided-demo token.
const copyReservations = new Map<symbol, string>();
const bumpCopy = (containerId: string) => {
  const key = `c:${containerId}`;
  scopeEpochs.set(key, (scopeEpochs.get(key) ?? 0) + 1);
  globalEpoch++;
  for (const l of listeners) l();
};
export function reserveCopyLiveScheduling(containerId: string): () => void {
  const token = Symbol('copy-live-scheduling');
  copyReservations.set(token, containerId); bumpCopy(containerId);
  return () => { if (!copyReservations.delete(token)) return; bumpCopy(containerId); };
}

const bump = (r: Reservation) => {
  for (const k of [`i:${r.instanceId}`, r.containerId ? `c:${r.containerId}` : null]) {
    if (k) scopeEpochs.set(k, (scopeEpochs.get(k) ?? 0) + 1);
  }
  globalEpoch++;
  for (const l of listeners) l();
};
const clean = (xs: ReadonlyArray<string | null | undefined> | undefined) =>
  new Set((xs ?? []).filter((x): x is string => !!x));

export function reserveDemoLiveScheduling(instanceId: string, containerId: string | null = null): symbol {
  const token = Symbol('demo-live-scheduling');
  const prev = reservation;
  reservation = { token, instanceId, containerId };
  if (prev) bump(prev);
  bump(reservation);
  return token;
}

/** Releases only the reservation this token created (a stale release never clears a newer one). */
export function releaseDemoLiveScheduling(token: symbol | null | undefined): void {
  if (!token || !reservation || reservation.token !== token) return;
  const r = reservation;
  reservation = null;
  bump(r);
}

const intersects = (r: Reservation, instanceIds: Set<string>, containerIds: Set<string>) =>
  instanceIds.has(r.instanceId) || (!!r.containerId && containerIds.has(r.containerId));

export function isLiveScopeSuspended(scope: LiveSaveScope): boolean {
  const containers = clean(scope.containerIds);
  return (!!reservation && intersects(reservation, clean(scope.instanceIds), containers))
    || [...copyReservations.values()].some(id => containers.has(id));
}
/** Back-compat single-instance query. */
export const isDemoLiveSchedulingSuspended = (instanceId: string | null | undefined): boolean =>
  isLiveScopeSuspended({ instanceIds: [instanceId] });

/** Epoch of reservations touching this scope only (unrelated scopes never change it). */
export function scopedLiveEpoch(scope: LiveSaveScope): string {
  const keys = [...clean(scope.instanceIds)].map((i) => `i:${i}`)
    .concat([...clean(scope.containerIds)].map((c) => `c:${c}`)).sort();
  return keys.map((k) => `${k}=${scopeEpochs.get(k) ?? 0}`).join(',');
}

/** Global change counter — only a re-render trigger for useSyncExternalStore. */
export const liveSchedulingEpoch = () => globalEpoch;
export const subscribeLiveSchedulingReservation = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/**
 * Admit + track a background save synchronously; null (never started) while
 * any instance/container of its scope is reserved.
 */
export function admitLiveSave<T>(scope: LiveSaveScope | string | null | undefined, work: () => Promise<T>): Promise<T> | null {
  const s: LiveSaveScope = typeof scope === 'object' && scope ? scope : { instanceIds: [scope as string | null | undefined] };
  if (isLiveScopeSuspended(s)) return null;
  const p = work();
  inFlight.set(p, { instanceIds: clean(s.instanceIds), containerIds: clean(s.containerIds) });
  void p.finally(() => inFlight.delete(p)).catch(() => undefined);
  return p;
}

/** Resolve once every admitted save intersecting `target` has settled (all saves if omitted). */
export async function drainLiveSaves(target?: { instanceId?: string; containerId?: string | null }): Promise<void> {
  const pick = () => [...inFlight.entries()]
    .filter(([, sc]) => !target || (!!target.instanceId && sc.instanceIds.has(target.instanceId)) || (!!target.containerId && sc.containerIds.has(target.containerId)))
    .map(([p]) => p);
  for (let batch = pick(); batch.length; batch = pick()) await Promise.allSettled(batch);
}

/**
 * Notice episode tracking the CURRENT conflict set. Keys are `<category>:<text>`.
 * - `reconcile(scopeKey, present, evaluated)`: keys of an evaluated category that
 *   are no longer present leave the episode (so a recurrence shows again); keys
 *   in `present` not yet shown are returned (shown once while they persist).
 *   Categories not evaluated this run keep their keys.
 * - A scope change clears everything.
 */
export function createNoticeEpisode() {
  let scope = '';
  const shown = new Set<string>();
  const cat = (k: string) => k.slice(0, k.indexOf(':'));
  return {
    reconcile(scopeKey: string, present: string[], evaluated: string[]): string[] {
      if (scopeKey !== scope) { scope = scopeKey; shown.clear(); }
      const ev = new Set(evaluated);
      const now = new Set(present);
      for (const k of [...shown]) if (ev.has(cat(k)) && !now.has(k)) shown.delete(k);
      const fresh = present.filter((k) => !shown.has(k));
      for (const k of fresh) shown.add(k);
      return [...new Set(fresh)];
    },
    /** Single key admission (used for save errors). */
    admit(scopeKey: string, key: string): boolean {
      if (scopeKey !== scope) { scope = scopeKey; shown.clear(); }
      if (shown.has(key)) return false;
      shown.add(key);
      return true;
    },
    clearCategory(category: string) { for (const k of [...shown]) if (cat(k) === category) shown.delete(k); },
    reset() { shown.clear(); },
  };
}

/** Test-only. */
export function __resetLiveSchedulingReservationForTests() { reservation = null; copyReservations.clear(); inFlight.clear(); scopeEpochs.clear(); globalEpoch++; for (const l of listeners) l(); }
